/**
 * Past sync runs for /admin/sync-history.
 *
 * `sync_jobs` has recorded every run since 2026-07-21 and nothing prunes it, but
 * no screen ever read more than one scalar out of it: `/settings` shows
 * `last_synced_at`, `/admin/sync-schedules` shows `last_run_at`, and
 * `lastSyncOutcome` lifts the note off the latest `no_data` row. "What happened
 * over the last few days" needed psql. These two RPCs are that answer.
 *
 * Read straight from Supabase with no HTTP endpoint, the same shape as
 * `aiUsageService` and `syncScheduleService`: `sync_history` and
 * `sync_history_daily` are SECURITY DEFINER and check `is_platform_master()`
 * themselves, so the database is the boundary.
 *
 * @module services/syncHistoryService
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";

/** One finished-or-running sync run. */
export interface SyncHistoryRow {
  id: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  /** null when the job never started, or started and has not finished. */
  durationSeconds: number | null;
  workspaceId: string | null;
  workspaceLabel: string;
  platform: string;
  accountId: string | null;
  accountLabel: string;
  /**
   * `schedule` | `backfill` | `manual`, read off the values actually written:
   * `enqueueSync.ts` stamps 'schedule' for a scheduled tick — NOT 'routine', which
   * is the word the UI uses for the same thing and appears nowhere in this column.
   * UNKNOWN_SOURCE for rows predating migration 103, which had no source at all.
   */
  source: string;
  status: string;
  /** null for a scheduled sync, which carries no explicit window. */
  startDate: string | null;
  endDate: string | null;
  attempts: number;
  entitiesProcessed: number;
  metricsWritten: number;
  errorMessage: string | null;
  requestedByLabel: string;
}

/** One day, one source. */
export interface SyncHistoryDay {
  day: string;
  source: string;
  jobs: number;
  succeeded: number;
  noData: number;
  failed: number;
  partial: number;
  active: number;
  entitiesProcessed: number;
  metricsWritten: number;
}

export interface SyncHistoryResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  /**
   * True only for Postgres 42501, which is the single code both RPC guards raise.
   * Kept apart from `error` because the page renders a different thing for each:
   * "you may not look here" is not "the load failed", and `/admin/ai/usage`
   * showing the master panel for any error at all is the looser version of this
   * that treats a dropped connection as a permission problem.
   */
  notAuthorized?: boolean;
}

/** Rows written before migration 103 added `source`; the column is NULL on those. */
export const UNKNOWN_SOURCE = "unknown";

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const str = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);

/**
 * A job whose account row is gone.
 *
 * `sync_jobs_account_id_fkey` is ON DELETE CASCADE, so disconnecting an account
 * deletes its jobs outright — this label is therefore NOT for that case, which
 * cannot reach a reader at all. It is for `account_id IS NULL`, a job enqueued
 * without one, and it says so rather than printing an empty cell that reads as a
 * rendering bug.
 */
export const NO_ACCOUNT_LABEL = "(ไม่ผูกกับบัญชี)";
/** No `requested_by` at all: the worker's own schedule tick, or a row inserted by an operator. */
export const SYSTEM_ACTOR_LABEL = "(ระบบสั่งเอง)";

/**
 * Who asked for this run.
 *
 * `requested_by` has NO foreign key — only `account_id` and `workspace_id` do —
 * so a deleted user leaves a live uuid whose join partner is gone. Falling back
 * on the missing email would file that run under "the system did it", which is a
 * different claim about a different actor. The id is still the attribution, so
 * the id decides and the first 8 characters are shown. Same mistake
 * `aiUsageService` already paid for once, same fix.
 */
const actorLabel = (id: string | null, email: string | null): string => {
  if (email) return email;
  if (id) return `ผู้ใช้ที่ถูกลบ · ${id.slice(0, 8)}`;
  return SYSTEM_ACTOR_LABEL;
};

const mapRow = (r: Record<string, unknown>): SyncHistoryRow => ({
  id: String(r.id ?? ""),
  createdAt: String(r.created_at ?? ""),
  startedAt: str(r.started_at),
  completedAt: str(r.completed_at),
  durationSeconds: typeof r.duration_seconds === "number" ? r.duration_seconds : null,
  workspaceId: str(r.workspace_id),
  // A workspace IS cascade-deleted along with its jobs, so a blank name here
  // means the column was null, not that the workspace vanished.
  workspaceLabel: str(r.workspace_name) ?? "(ไม่ระบุ workspace)",
  platform: str(r.platform) ?? "—",
  accountId: str(r.account_id),
  accountLabel: str(r.account_name) ?? NO_ACCOUNT_LABEL,
  source: str(r.source) ?? UNKNOWN_SOURCE,
  status: str(r.status) ?? "—",
  startDate: str(r.start_date),
  endDate: str(r.end_date),
  attempts: num(r.attempts),
  entitiesProcessed: num(r.entities_processed),
  metricsWritten: num(r.metrics_written),
  errorMessage: str(r.error_message),
  requestedByLabel: actorLabel(str(r.requested_by), str(r.requested_by_email)),
});

const mapDay = (r: Record<string, unknown>): SyncHistoryDay => ({
  day: String(r.day ?? ""),
  source: str(r.source) ?? UNKNOWN_SOURCE,
  jobs: num(r.jobs),
  succeeded: num(r.succeeded),
  noData: num(r.no_data),
  failed: num(r.failed),
  partial: num(r.partial),
  active: num(r.active),
  entitiesProcessed: num(r.entities_processed),
  metricsWritten: num(r.metrics_written),
});

/** Postgres `insufficient_privilege`, the only code either guard raises. */
const NOT_AUTHORIZED = "42501";

const failure = <T,>(error: { message?: string; code?: string } | null, fallback: string): SyncHistoryResult<T> => ({
  success: false,
  error: error?.message ?? fallback,
  ...(error?.code === NOT_AUTHORIZED ? { notAuthorized: true } : {}),
});

/**
 * The detail list, newest first.
 *
 * `limit` reaches the database rather than being applied here: the measured worst
 * day was 305 jobs (2026-10-06) and a 36-month backfill is 37 per account, so
 * trimming client-side would mean transferring an incident to throw most of it
 * away.
 */
export const listSyncHistory = async (
  days: number,
  limit: number,
): Promise<SyncHistoryResult<SyncHistoryRow[]>> => {
  try {
    // Literal RPC name, deliberately not hoisted into a helper: rpcArgumentNames.test.ts
    // reads source text and fails on any `.rpc()` whose first argument isn't a literal.
    const { data, error } = await getSupabaseClient().rpc("sync_history", {
      p_days: days,
      p_limit: limit,
    });
    if (error) return failure(error, "Failed to load sync history");

    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return { success: true, data: rows.map(mapRow) };
  } catch (e) {
    logError("SyncHistory", "sync_history failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load sync history" };
  }
};

/** The per-day rollup the page leads with, so one `failed` among 305 rows is not buried. */
export const listSyncHistoryDaily = async (
  days: number,
): Promise<SyncHistoryResult<SyncHistoryDay[]>> => {
  try {
    const { data, error } = await getSupabaseClient().rpc("sync_history_daily", { p_days: days });
    if (error) return failure(error, "Failed to load sync summary");

    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return { success: true, data: rows.map(mapDay) };
  } catch (e) {
    logError("SyncHistory", "sync_history_daily failed", e);
    return { success: false, error: e instanceof Error ? e.message : "Failed to load sync summary" };
  }
};
